/** Launch & mapping (plan §2 S1) — the tour record's first panel: what makes a tour
 *  reachable and launched, in the order an operator checks it.
 *
 *  1. External IDs — the identifiers table, then the System block (UUID, link, code, the
 *     last 20 import runs);
 *  2. Launch policy — the picker, the operator default, whether the tenant enforces
 *     policies, the effective flags;
 *  3. Access and system tags;
 *  4. Tag categories — the table, with `use_operator_defaults` beside it;
 *  5. Readiness — the same checks the launch band's nodes show, each a link to its field.
 *
 *  One SectionCard, the `tour#launch` anchor; each block is an anchor of its own
 *  (`tour#mapping`, `#import`, `#policy`, `#access`, `#tag-categories`, `#readiness`) —
 *  the band's nodes land on them. Same DOM in view and edit mode. */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, ButtonBase, Typography } from '@mui/material';

import { SectionCard } from '@/components/ReadView';
import { useRecordEditor } from '@/features/records/editor/RecordEditorProvider';

import { sectionAnchorId, TOUR_SECTIONS } from '../registry';
import { useReadiness } from '../useReadiness';

import { BlockAnchor } from './BlockAnchor';
import { IdentifiersBlock } from './IdentifiersBlock';
import { PolicyBlock } from './PolicyBlock';
import { TagCategoriesBlock } from './TagCategoriesBlock';
import { TourFieldGrid } from './TourSectionPanel';

const SECTION = TOUR_SECTIONS.find((s) => s.id === 'launch')!;

function Rule() {
  return <Box sx={{ borderTop: 1, borderColor: 'divider' }} />;
}

export function LaunchMappingPanel() {
  return (
    <Box
      component="section"
      id={sectionAnchorId('launch')}
      tabIndex={-1}
      aria-label={SECTION.title}
      data-tour-section="launch"
      sx={{
        scrollMarginTop: 'var(--record-spine-height, 0px)',
        outline: 'none',
        borderRadius: 1,
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
        '& > .MuiCard-root': { mb: 0 },
      }}
    >
      <SectionCard title={SECTION.title}>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <IdentifiersBlock />
          <Rule />
          <PolicyBlock />
          <Rule />
          <BlockAnchor id="access" label="Access and system tags">
            <Box sx={{ display: 'grid', gap: 0.5 }}>
              <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
                Access and system tags
              </Typography>
              <TourFieldGrid keys={['atag', 'tags']} />
            </Box>
          </BlockAnchor>
          <Rule />
          <TagCategoriesBlock />
          <Rule />
          <ReadinessBlock />
        </Box>
      </SectionCard>
    </Box>
  );
}

/** The launch-readiness checks, each a link to the field it is about. */
export function ReadinessBlock() {
  const checks = useReadiness();
  const { focusField } = useRecordEditor();
  return (
    <BlockAnchor id="readiness" label="Readiness">
      <Box sx={{ display: 'grid', gap: 0.5 }} data-readiness>
        <Typography variant="caption" component="h3" color="text.secondary" sx={{ m: 0, fontWeight: 600 }}>
          Readiness
          <Box component="span" sx={{ fontWeight: 400 }}>
            {' '}
            · {checks.length === 0 ? 'nothing to check' : `${checks.length} to check`}
          </Box>
        </Typography>
        {checks.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 16, color: 'primary.main' }} />
            Identifiers, launch policy and departures look ready.
          </Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 }}>
            {checks.map((c) => {
              const body = (
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', textAlign: 'left' }}>
                  <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16, color: 'warning.main', mt: '2px' }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {c.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.detail}
                    </Typography>
                  </Box>
                </Box>
              );
              const target = c.target;
              return (
                <Box component="li" key={c.key} data-check={c.code}>
                  {target ? (
                    <ButtonBase
                      onClick={() => focusField(target.rec, target.path)}
                      sx={{
                        display: 'block',
                        width: '100%',
                        borderRadius: 1,
                        px: 0.5,
                        py: 0.25,
                        '&:hover': { bgcolor: 'action.hover' },
                        '&.Mui-focusVisible': { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.main}` },
                      }}
                    >
                      {body}
                    </ButtonBase>
                  ) : (
                    <Box sx={{ px: 0.5, py: 0.25 }}>{body}</Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </BlockAnchor>
  );
}
